// shell/components/entity-fields/form-renderers/slider-input.ts

import { createSlider } from '../../../utils/pane-ui';
import type { SliderFormField } from '../../entity-view/types';
import type { FormRendererResult } from './types';

/**
 * Renders a slider (range input) paired with a number input.
 * Wraps the shell's `createSlider` primitive so toolbar zoom controls
 * and form-field sliders share the same range styling and behavior.
 */
export function renderSliderInput<T>(
    field: SliderFormField<T>,
    initialValue: unknown,
    onChange: (value: unknown) => void
): FormRendererResult {
    const numValue = typeof initialValue === 'number' ? initialValue : field.min;

    const slider = createSlider({
        min: field.min,
        max: field.max,
        step: field.step ?? 1,
        value: numValue,
        disabled: field.disabled,
        valueDisplay: { kind: 'input' },
        onInput: v => onChange(v),
    });

    // The form-field wrapper class is preserved for any layout rules
    // that target it externally; cs-slider supplies the inner row.
    slider.element.classList.add('ev-field-input', 'ev-field-input--slider');

    return {
        element: slider.element,
        getValue: () => slider.getValue(),
        setValue: (value: unknown) => {
            const val = typeof value === 'number' ? value : field.min;
            slider.setValue(val);
        },
        validate: async () => true,
        focus: () => slider.rangeInput.focus(),
        dispose: () => slider.destroy(),
        setDisabled: (disabled: boolean) => slider.setDisabled(disabled),
    };
}
